/**
 * Dev Console → Arche Management → Store: app screenshots (ADR-043).
 *
 * Waiting for review: screenshots the image screen was unsure of, held where
 * no one else can see them. Approve shows one on the app's store page;
 * reject (with a reason) drops it. Admins and plugin approvers decide.
 *
 * Built-in arches: Fias's own arches have no manifest, so their screenshots
 * are set here — new uploads go through the same image screen, billed to the
 * arche's payer (the Fias plugin billing account), and one the screen is
 * unsure of joins the queue above. A plugin's screenshots come from its
 * manifest, so plugins are not offered.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, ImageOff, ImagePlus, Loader2, Save, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import api, { ApiError, type AdminArcheScreenshot, type AdminScreenshotReview } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Must match the server (STORE_SCREENSHOT_* in @fias/db-types). */
const SCREENSHOTS_MAX = 10;
const ALT_MAX = 120;
const MAX_BYTES = 2 * 1024 * 1024;
const TYPES = 'image/png,image/jpeg,image/webp';

function errorMessage(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong';
}

/** A held screenshot, fetched with the reviewer's session and shown from memory. */
function HeldImage({ reviewId, label }: { reviewId: string; label: string }): ReactElement {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    void api.adminGetScreenshotReviewImage(reviewId).then((blob) => {
      if (cancelled) return;
      if (!blob) {
        setFailed(true);
        return;
      }
      url = URL.createObjectURL(blob);
      setSrc(url);
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [reviewId]);
  if (src) {
    return (
      <img
        src={src}
        alt={label}
        className="h-40 w-auto max-w-full rounded-lg border object-contain"
      />
    );
  }
  return (
    <div className="flex h-40 w-28 items-center justify-center rounded-lg border bg-muted">
      {failed ? (
        <ImageOff className="h-6 w-6 text-muted-foreground" aria-label="Image unavailable" />
      ) : (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The review queue
// ---------------------------------------------------------------------------

function ReviewRow({ review }: { review: AdminScreenshotReview }): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const done = () => void queryClient.invalidateQueries({ queryKey: queryKeys.store.all });
  const approve = useMutation({
    mutationFn: () => api.adminApproveScreenshotReview(review.reviewId),
    onSuccess: ({ outcome }) => {
      done();
      toast({
        title:
          outcome === 'approved'
            ? `Screenshot shown on ${review.archeName}`
            : 'No longer needed: the app’s current release does not show it',
      });
    },
    onError: (err) =>
      toast({ title: 'Not approved', description: errorMessage(err), variant: 'destructive' }),
  });
  const reject = useMutation({
    mutationFn: () => api.adminRejectScreenshotReview(review.reviewId, reason.trim()),
    onSuccess: () => {
      done();
      toast({ title: 'Screenshot rejected' });
    },
    onError: (err) =>
      toast({ title: 'Not rejected', description: errorMessage(err), variant: 'destructive' }),
  });
  const busy = approve.isPending || reject.isPending;
  return (
    <li className="flex flex-col gap-3 p-3 sm:flex-row">
      <HeldImage reviewId={review.reviewId} label={`Screenshot for ${review.archeName}`} />
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <div className="text-sm font-medium">{review.archeName}</div>
          <div className="text-xs text-muted-foreground">
            {review.width}×{review.height} · {new Date(review.createdAt).toLocaleDateString()}
            {review.screenReason && ` · ${review.screenReason}`}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="gap-1"
            disabled={busy}
            onClick={() => approve.mutate()}
            aria-label={`Approve the screenshot for ${review.archeName}`}
          >
            <Check className="h-4 w-4" />
            Approve
          </Button>
          <Input
            aria-label={`Why reject the screenshot for ${review.archeName}`}
            placeholder="Why reject (for the audit log)"
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="h-8 max-w-xs flex-1"
          />
          <Button
            size="sm"
            variant="destructive"
            className="gap-1"
            disabled={busy || !reason.trim()}
            onClick={() => reject.mutate()}
            aria-label={`Reject the screenshot for ${review.archeName}`}
          >
            <X className="h-4 w-4" />
            Reject
          </Button>
        </div>
      </div>
    </li>
  );
}

export function ScreenshotReviewQueueCard(): ReactElement {
  const queue = useQuery({
    queryKey: queryKeys.store.adminScreenshotReviews(),
    queryFn: () => api.adminListScreenshotReviews(),
  });
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Screenshots waiting for review{queue.data ? ` (${queue.data.pending})` : ''}
        </CardTitle>
        <CardDescription>
          The image screen was unsure of these, so no one sees them until a person decides. The rest
          of each app’s release is already live.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {queue.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
        {queue.isError && (
          <p className="text-sm text-destructive">Didn’t load: {errorMessage(queue.error)}</p>
        )}
        {queue.data && queue.data.reviews.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing is waiting.</p>
        )}
        {queue.data && queue.data.reviews.length > 0 && (
          <ul className="divide-y rounded-md border">
            {queue.data.reviews.map((review) => (
              <ReviewRow key={review.reviewId} review={review} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// A built-in arche's screenshots
// ---------------------------------------------------------------------------

/** One screenshot in the draft: one already on the arche, or an upload. */
type DraftShot =
  | { kind: 'kept'; key: string; shot: AdminArcheScreenshot; alt: string }
  | { kind: 'new'; key: string; stagingKey: string; previewUrl: string; alt: string };

async function uploadScreenshot(file: File): Promise<string> {
  const { uploadUrl, stagingKey } = await api.adminGetScreenshotUploadUrl({
    mimeType: file.type,
    sizeBytes: file.size,
  });
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed (status ${response.status})`);
  return stagingKey;
}

function BuiltInArcheScreenshotsEditor({ archId }: { archId: string }): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: queryKeys.store.adminArcheScreenshots(archId),
    queryFn: () => api.adminGetArcheScreenshots(archId),
  });
  const [draft, setDraft] = useState<DraftShot[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const saved: DraftShot[] = useMemo(
    () =>
      (current.data?.screenshots ?? []).map((shot) => ({
        kind: 'kept' as const,
        key: shot.contentSha256,
        shot,
        alt: shot.alt ?? '',
      })),
    [current.data],
  );
  const shots = draft ?? saved;

  const save = useMutation({
    mutationFn: () =>
      api.adminSetArcheScreenshots(
        archId,
        shots.map((s) =>
          s.kind === 'kept'
            ? { contentSha256: s.shot.contentSha256, alt: s.alt.trim() || null }
            : { stagingKey: s.stagingKey, alt: s.alt.trim() || null },
        ),
      ),
    onSuccess: ({ live, held }) => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.all });
      toast({
        title: 'Screenshots saved',
        description: held > 0 ? `${live} shown; ${held} waiting for review.` : `${live} shown.`,
      });
    },
    onError: (err) =>
      toast({ title: 'Not saved', description: errorMessage(err), variant: 'destructive' }),
  });

  const add = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const added: DraftShot[] = [];
    try {
      for (const file of Array.from(files).slice(0, SCREENSHOTS_MAX - shots.length)) {
        if (file.size > MAX_BYTES) throw new Error(`${file.name} is over 2 MB`);
        const stagingKey = await uploadScreenshot(file);
        added.push({
          kind: 'new',
          key: stagingKey,
          stagingKey,
          previewUrl: URL.createObjectURL(file),
          alt: '',
        });
      }
    } catch (err) {
      toast({ title: 'Not added', description: errorMessage(err), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (added.length > 0) setDraft([...shots, ...added]);
    }
  };

  const move = (index: number, by: -1 | 1) => {
    const to = index + by;
    if (to < 0 || to >= shots.length) return;
    const next = [...shots];
    [next[index], next[to]] = [next[to], next[index]];
    setDraft(next);
  };

  if (current.isLoading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (current.isError) {
    return <p className="text-sm text-destructive">Didn’t load: {errorMessage(current.error)}</p>;
  }

  return (
    <div className="space-y-3">
      {shots.length === 0 && <p className="text-sm text-muted-foreground">No screenshots yet.</p>}
      <ol className="space-y-2">
        {shots.map((s, index) => {
          const name = `Screenshot ${index + 1}`;
          return (
            <li key={s.key} className="flex flex-col gap-2 rounded-md border p-2 sm:flex-row">
              {s.kind === 'new' ? (
                <img src={s.previewUrl} alt={name} className="h-28 w-auto rounded object-contain" />
              ) : s.shot.url ? (
                <img
                  src={s.shot.urlSmall ?? s.shot.url}
                  alt={name}
                  className="h-28 w-auto rounded object-contain"
                />
              ) : s.shot.reviewId ? (
                <HeldImage reviewId={s.shot.reviewId} label={name} />
              ) : (
                <div className="h-28 w-20 rounded bg-muted" />
              )}
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{name}</span>
                  {s.kind === 'new' && <Badge variant="outline">New: screened when saved</Badge>}
                  {s.kind === 'kept' && s.shot.status === 'held' && (
                    <Badge variant="outline">Waiting for review</Badge>
                  )}
                  <span className="ml-auto flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label={`Move ${name} up`}
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={index === shots.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label={`Move ${name} down`}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setDraft(shots.filter((x) => x.key !== s.key))}
                      aria-label={`Remove ${name}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </span>
                </div>
                <Input
                  aria-label={`What ${name} shows`}
                  placeholder="What it shows (for people who can’t see it)"
                  maxLength={ALT_MAX}
                  value={s.alt}
                  onChange={(e) =>
                    setDraft(
                      shots.map((x) => (x.key === s.key ? { ...x, alt: e.target.value } : x)),
                    )
                  }
                  className="h-8 text-sm"
                />
              </div>
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary">
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ImagePlus className="h-4 w-4" />
          )}
          Add screenshots ({shots.length}/{SCREENSHOTS_MAX})
          <input
            type="file"
            accept={TYPES}
            multiple
            className="sr-only"
            aria-label="Add screenshots"
            disabled={uploading || shots.length >= SCREENSHOTS_MAX}
            onChange={(e) => {
              void add(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
        <Button
          size="sm"
          className="gap-1.5"
          disabled={draft === null || save.isPending || uploading}
          onClick={() => save.mutate()}
          aria-label="Save screenshots"
        >
          {save.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save
        </Button>
      </div>
    </div>
  );
}

export function BuiltInArcheScreenshotsCard(): ReactElement {
  const [archId, setArchId] = useState('');
  const picker = useQuery({
    queryKey: queryKeys.store.adminPicker(),
    queryFn: () => api.adminGetStorePicker(),
  });
  // Built-in arches only: their ids start `arche_`; a listing is not one.
  const builtIn = (picker.data?.items ?? []).filter(
    (card) => card.kind === 'arche' && card.archId.startsWith('arche_'),
  );
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Screenshots for built-in arches</CardTitle>
        <CardDescription>
          Fias’s own arches have no manifest, so their screenshots are set here. Up to 10, in order;
          PNG, JPEG or WebP, at most 2 MB, 320–4096 px. New ones are checked by the image screen
          when you save, billed to the Fias plugin billing account; one it is unsure of waits for
          review. A plugin’s screenshots come from its manifest.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="screenshots-arche">Arche</Label>
          <select
            id="screenshots-arche"
            className="h-9 w-full max-w-sm rounded-md border bg-background px-2 text-sm"
            value={archId}
            onChange={(e) => setArchId(e.target.value)}
          >
            <option value="">Choose a built-in arche…</option>
            {builtIn.map((card) => (
              <option key={card.archId} value={card.archId}>
                {card.title}
              </option>
            ))}
          </select>
        </div>
        {archId && <BuiltInArcheScreenshotsEditor key={archId} archId={archId} />}
      </CardContent>
    </Card>
  );
}
